import * as React from "react";
import { Namespace, useT } from "@racoon/i18n";
import {
  Button,
  Input,
  Label,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  ModalClose,
  Switch,
  useToast,
} from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { BIN_LENGTH, isBlank, isRequiredBinValid, validateBin } from "@racoon/lib";
import {
  useCreateOrganizationMutation,
  useGetOrganizationQuery,
  useUpdateOrganizationMutation,
  useUpdateOrganizationSettingsMutation,
} from "~/shared/api/organization/organization.grpc";
import { useAuthSession } from "~/shared/hooks/auth.session.admin";
import { useOrganizationBinDuplicate } from "~/shared/hooks/use-bin-duplicate";

const BIN_MESSAGE_ID = "organization-dialog-bin-message";

type OrganizationDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
} & ({ mode: "create" } | { mode: "edit"; org: ConnectProto.Organization | null });

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between w-full">
      <Label className="text-sm font-medium text-[#1d1f23] shrink-0">{label}</Label>
      <div className="w-[219px] shrink-0">{children}</div>
    </div>
  );
}

export function OrganizationDialog(props: OrganizationDialogProps) {
  const { open, onOpenChange } = props;
  const org = props.mode === "edit" ? props.org : null;
  const isEdit = props.mode === "edit";

  const t = useT();
  const [createOrg, { isLoading: isCreating }] = useCreateOrganizationMutation();
  const [updateOrg, { isLoading: isUpdating }] = useUpdateOrganizationMutation();
  const [updateSettings, { isLoading: isUpdatingSettings }] =
    useUpdateOrganizationSettingsMutation();
  const isLoading = isCreating || isUpdating || isUpdatingSettings;
  const { toast } = useToast();

  // Responsible zones: a service provider's flag, platform admins only (the backend drops it
  // from anyone else's UpdateSetting). GetSetting reads the caller's own org, so this org's
  // settings come with GetOrganization, read afresh on every opening.
  const { isAdmin } = useAuthSession();
  const hasZoneFlag = isAdmin && org?.type === ConnectProto.OrganizationType.SERVICE_PROVIDER;
  const { currentData: orgWithSettings, isFetching: isFetchingSettings } = useGetOrganizationQuery(
    { organizationId: org?.id ?? BigInt(0) },
    { skip: !open || !hasZoneFlag, refetchOnMountOrArgChange: true },
  );
  const storedZoneEnabled = orgWithSettings?.organizationSettings?.responsibleZoneEnabled ?? false;
  const settingsLoaded = !!orgWithSettings?.organizationSettings && !isFetchingSettings;
  // Saved with the dialog's Save; Cancel discards it.
  const [zoneEnabled, setZoneEnabled] = React.useState(false);
  React.useEffect(() => {
    if (open) setZoneEnabled(storedZoneEnabled);
  }, [open, storedZoneEnabled]);

  const [name, setName] = React.useState("");
  const [jurAddress, setJurAddress] = React.useState("");
  const [bin, setBin] = React.useState("");
  const [type, setType] = React.useState<ConnectProto.OrganizationType | null>(null);

  // Prefill from the org when editing; reset when creating / switching target.
  React.useEffect(() => {
    setName(org?.name ?? "");
    setJurAddress(org?.jurAddress ?? "");
    setBin(org?.bin ?? "");
    setType(null);
  }, [org]);

  const binIssue = validateBin(bin);
  // Looked up once the BIN is changed: an org already sharing its BIN must stay editable.
  // A branch is never looked up — it shares its parent's BIN by design.
  const binUnchanged = bin.trim() === (org?.bin ?? "").trim();
  const {
    duplicate: binDuplicate,
    pending: binCheckPending,
    failed: binCheckFailed,
  } = useOrganizationBinDuplicate({
    type: org ? org.type : type,
    bin,
    excludeOrgId: org?.id,
    skip: binUnchanged || !!org?.parentId,
  });
  const canSubmit =
    isRequiredBinValid(bin) &&
    !binDuplicate &&
    !binCheckPending &&
    !binCheckFailed &&
    (isEdit ? !!name : !!name && type != null);
  // One message under the BIN, the most pressing first. A stored org saved without a BIN
  // says why Save stays disabled.
  const binMessage = binIssue
    ? t(Namespace.Validation, binIssue.key, binIssue.vars)
    : binDuplicate
      ? t(Namespace.Organizations, "binTaken", {
          name: binDuplicate.name?.trim() || t(Namespace.Common, "unnamed"),
        })
      : binCheckFailed
        ? t(Namespace.Organizations, "binCheckFailed")
        : isEdit && isBlank(bin)
          ? t(Namespace.Validation, "required")
          : undefined;

  const submit = async () => {
    try {
      if (org) {
        await updateOrg({ id: org.id, name, jurAddress, bin: bin.trim() }).unwrap();
        if (hasZoneFlag && settingsLoaded && zoneEnabled !== storedZoneEnabled) {
          // Settings id is the organization id. Errors are toasted by GlobalErrorToaster.
          const saved = await updateSettings({
            id: org.id,
            responsibleZoneEnabled: zoneEnabled,
          }).unwrap();
          // A backend without the flag answers OK but keeps the old value.
          const applied = (saved.responsibleZoneEnabled ?? false) === zoneEnabled;
          toast({
            title: t(
              Namespace.Organizations,
              applied ? "responsibleZone.saved" : "responsibleZone.notApplied",
            ),
            variant: applied ? undefined : "destructive",
          });
          // Kept open, so the admin sees the switch as it was sent and can retry
          if (!applied) return;
        }
      } else {
        await createOrg({ name, jurAddress, bin: bin.trim(), type: type ?? undefined }).unwrap();
      }
      onOpenChange(false);
      if (!isEdit) {
        setName("");
        setJurAddress("");
        setBin("");
        setType(null);
      }
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="custom" className="md:!w-[480px]">
        <ModalHeader>
          <ModalTitle>
            {t(Namespace.Organizations, isEdit ? "editTitle" : "createTitle")}
          </ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-4">
          <FieldRow label={t(Namespace.Organizations, "name")}>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t(Namespace.Organizations, "namePlaceholder")}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={t(Namespace.Organizations, "jurAddress")}>
            <Input
              value={jurAddress}
              onChange={(e) => setJurAddress(e.target.value)}
              placeholder={t(Namespace.Organizations, "jurAddressPlaceholder")}
              className="w-full"
            />
          </FieldRow>

          <FieldRow label={t(Namespace.Organizations, "bin")}>
            <Input
              value={bin}
              onChange={(e) => setBin(e.target.value)}
              placeholder="123456789012"
              maxLength={BIN_LENGTH}
              inputMode="numeric"
              aria-invalid={!!binMessage}
              aria-describedby={binMessage ? BIN_MESSAGE_ID : undefined}
              className="w-full"
            />
            {binMessage && (
              <p id={BIN_MESSAGE_ID} className="pt-1 text-xs text-text-error">
                {binMessage}
              </p>
            )}
          </FieldRow>

          {!isEdit && (
            <FieldRow label={t(Namespace.Organizations, "type")}>
              <Select
                value={type != null ? String(type) : ""}
                onValueChange={(v) => setType(Number(v))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={t(Namespace.Organizations, "selectType")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={String(ConnectProto.OrganizationType.SERVICE_PROVIDER)}>
                    {t(Namespace.Organizations, "type.providerFull")}
                  </SelectItem>
                  <SelectItem value={String(ConnectProto.OrganizationType.DISPOSAL_SITE_OPERATOR)}>
                    {t(Namespace.Organizations, "tabs.disposal")}
                  </SelectItem>
                  <SelectItem value={String(ConnectProto.OrganizationType.GOVERNMENT)}>
                    {t(Namespace.Organizations, "tabs.government")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </FieldRow>
          )}

          {/* Zones are a service provider's: its divisions serve one another's sites. */}
          {hasZoneFlag && (
            <div className="flex items-start justify-between gap-4 w-full">
              <div className="space-y-1">
                <Label
                  htmlFor="responsible-zone-switch"
                  className="text-sm font-medium text-[#1d1f23]"
                >
                  {t(Namespace.Organizations, "responsibleZone.label")}
                </Label>
                <p className="text-xs text-text-secondary">
                  {t(Namespace.Organizations, "responsibleZone.hint")}
                </p>
              </div>
              <Switch
                id="responsible-zone-switch"
                checked={zoneEnabled}
                onCheckedChange={setZoneEnabled}
                disabled={!settingsLoaded || isLoading}
              />
            </div>
          )}
        </ModalBody>
        <ModalFooter className="justify-end">
          <ModalClose asChild>
            <Button variant="secondary">{t(Namespace.Common, "cancel")}</Button>
          </ModalClose>
          <Button onClick={submit} disabled={!canSubmit || isLoading}>
            {t(Namespace.Common, isEdit ? "save" : "create")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
